import { Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, Skeleton } from '@/components/ui'
import { useTimeLogs } from '@/hooks'
import type { TimeLog } from '@/api'

/**
 * Format a duration in minutes to a readable string like "2h 15m" or "45m".
 */
function formatDuration(mins: number): string {
  if (mins <= 0) return '0m'
  const hours = Math.floor(mins / 60)
  const minutes = mins % 60
  if (hours === 0) return `${minutes}m`
  if (minutes === 0) return `${hours}h`
  return `${hours}h ${minutes}m`
}

/**
 * Format an ISO date string to a readable date.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function TimeLogRow({ log }: { log: TimeLog }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDate(log.logged_at)}
          </span>
          <span className="text-sm font-medium truncate">
            {log.notes || 'No description'}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground capitalize">
            {log.source}
          </span>
        </div>
      </div>
      <div className="ml-4 shrink-0">
        <span className="text-sm font-semibold tabular-nums">
          {formatDuration(log.duration_mins)}
        </span>
      </div>
    </div>
  )
}

function TimeLogSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center justify-between py-3 px-4 rounded-lg border">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-3 w-16" />
          </div>
          <Skeleton className="h-4 w-14 ml-4" />
        </div>
      ))}
    </div>
  )
}

export function TimeLogs() {
  const { data, isLoading, isError, error } = useTimeLogs()

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Time Log</h1>
        <p className="text-muted-foreground">
          Track time spent on tasks and projects.
        </p>
      </div>

      {/* Main content */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <TimeLogSkeleton />}

          {isError && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive">Failed to load time logs</p>
              <p className="text-sm text-muted-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {(!data?.items || data.items.length === 0) ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No time entries yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Start the timer or log time manually to see entries here.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.items.map((log) => (
                    <TimeLogRow key={log.id} log={log} />
                  ))}
                </div>
              )}

              {data && data.items.length > 0 && (
                <p className="text-xs text-muted-foreground text-center mt-4">
                  Showing {data.items.length} of {data.total} entries
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
